import { LogoutPopupComponent } from '../components/logout-popup/logout-popup.component';
import { environment } from '../../environments/environment';
import { ClaimsService } from '../services/claims.service';
import { StateService } from '../services/state.service';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})

export class SessionTimeoutService {
    private inactivityTimeoutId: any;
    private activeSessionTimeoutId: any;
    private readonly INACTIVITY_DURATION = environment.maxInactivityDurationMin * 60 * 1000;
    private readonly ACTIVE_SESSION_DURATION = environment.maxActiveSessionDurationMin * 60 * 1000;

    constructor(
        private msalService: MsalService,
        private dialog: MatDialog,
        private stateService: StateService,
        private claimsService: ClaimsService
    ) {
        this.startTracking();
    }

    private startTracking(): void {
        this.stateService.$loginDisplay.subscribe((isLoggedIn) => {
            if (isLoggedIn) {
                this.startTimers();
                this.addUserActivityListeners();
            } 
            else {
                this.clearTimers();
                this.removeUserActivityListeners();
            }
        });
    }

    private addUserActivityListeners(): void {
        ['click', 'keypress', 'scroll', 'mousemove'].forEach(event => {
            document.addEventListener(event, () => this.resetInactivityTimer());
        });
    }

    private removeUserActivityListeners(): void {
        ['click', 'keypress', 'scroll', 'mousemove'].forEach(event => {
            document.removeEventListener(event, () => this.resetInactivityTimer());
        });
    }

    private resetInactivityTimer(): void {
        if (!this.msalService.instance.getAllAccounts().length) {
            return;
        }

        if (this.inactivityTimeoutId) {
            clearTimeout(this.inactivityTimeoutId);
        }

        this.inactivityTimeoutId = setTimeout(() => {
            this.showLogoutPopup('You have been inactive for too long.');
        }, this.INACTIVITY_DURATION);
    }

    private startTimers(): void {
        this.resetInactivityTimer();
        this.startActiveSessionTimer();
    }

    private startActiveSessionTimer(): void {
        if (this.activeSessionTimeoutId) {
            clearTimeout(this.activeSessionTimeoutId);
        }

        this.activeSessionTimeoutId = setTimeout(() => {
            this.showLogoutPopup('Your session has expired.');
        }, this.ACTIVE_SESSION_DURATION);
    }

    private showLogoutPopup(message: string): void {
        const dialogRef = this.dialog.open(LogoutPopupComponent, {
            data: { message },
            width: '400px',
            disableClose: true
        });
        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.performLogout();
            }
            else {
                this.resetTimers();
            }
        });
    }

    public performLogout(): void {
        const claim = this.stateService.claim;
        if (claim && claim.claimReference) {
            this.claimsService.loadGuid(claim.claimReference).subscribe({
                next: (response) => {
                    const guid = response.urlGuid?.toUpperCase();
                    if (guid) {
                        const redirectUrl = `/${guid}/login`;
                        this.updateLogoutState(true);
                        this.msalService.logoutRedirect({ postLogoutRedirectUri: redirectUrl });
                    }
                    else {
                        console.error('Error: Received invalid GUID.');
                        this.performFallbackLogout();
                    }
                },
                error: (error) => {
                    console.error('Error fetching GUID:', error);
                    this.performFallbackLogout();
                }
            });
        }
        else {
            console.warn('No claim reference available. Performing fallback logout.');
            this.performFallbackLogout();
        }
    }

    private performFallbackLogout(): void {
        this.updateLogoutState(false);
        this.msalService.logoutRedirect({ postLogoutRedirectUri: `/${environment.defaultGuid}/login` });
    }

    private updateLogoutState(isValidLogout: boolean): void {
        this.stateService.$loginDisplay.next(isValidLogout);
        this.stateService.isSigningUp = isValidLogout;
        localStorage.setItem('signingUp', JSON.stringify(isValidLogout));
    }

    public resetTimers(): void {
        this.resetInactivityTimer();
        this.startActiveSessionTimer();
    }

    private clearTimers(): void {
        if (this.inactivityTimeoutId) {
            clearTimeout(this.inactivityTimeoutId);
        }
        if (this.activeSessionTimeoutId) {
            clearTimeout(this.activeSessionTimeoutId);
        }
    }
}
